/* eslint-disable react-refresh/only-export-components */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { fetchIncomingTransactionById } from '@/api/transactions';
import type { OutgoingTransactionDetailLine } from '@/api/transactions/types';
import { IncomingTransactionDetailHeader } from '@/app/(client)/transactions/-components/IncomingTransactionDetailHeader';
import { incomingTransactionDetailKeys } from '@/app/(client)/transactions/-hooks/useIncomingTransactionDetail';
import { useReceiveIncomingPage } from '@/app/(client)/transactions/-hooks/useReceiveIncomingPage';
import { useReceiveIncomingTransaction } from '@/app/(client)/transactions/-hooks/useReceiveIncomingTransaction';
import { ConnectedPartiesCard } from '@/components/shared/ConnectedPartiesCard';
import { PartyRow } from '@/components/shared/ConnectedPartiesCard/PartyRow';
import { DataTable } from '@/components/shared/DataTable';
import { DocumentRulesTable } from '@/components/shared/DocumentRulesTable';
import { FieldCard, FieldRow } from '@/components/shared/FieldLayout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslator } from '@/hooks/useTranslator';
import { SHIPMENT_METHOD_LABELS } from '@/lib/constants/fiberTransactions';
import { formatDateDisplay } from '@/lib/utils/formatters';
import { queryClient } from '@/lib/utils/queryClient';

export const Route = createFileRoute(
  '/(client)/transactions/(pages)/(create)/incoming/receive/$txId/',
)({
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: incomingTransactionDetailKeys.detail(params.txId),
      queryFn: () => fetchIncomingTransactionById(params.txId),
    }),
  component: ReceiveIncomingPage,
});

function ReceiveIncomingPage() {
  const { txId } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'fiberTransactions' });
  const navigate = useNavigate();
  const {
    tx,
    isLoading,
    rules,
    documents,
    handleFileSelected,
    handleFileUploaded,
  } = useReceiveIncomingPage(txId);
  const receiveMutation = useReceiveIncomingTransaction();

  async function handleReceive() {
    if (!tx) return;
    try {
      const uploadedDocs = documents
        .filter(d => d.fileId)
        .map(d => ({ fileId: d.fileId!, type: d.type, name: d.name }));
      const res = await receiveMutation.mutateAsync({
        transactionType: 'INCOMING',
        incomingFiberTransactionId: tx.id,
        transactionNumber: tx.transactionNumber,
        transportationMethod: tx.transportationMethod,
        transportationIdentification: tx.transportationIdentification,
        transactionDate: tx.transactionDate,
        withCompany: {
          id: tx.withCompany.id,
          featuredFacilityProcessId: tx.withCompanyFacilityProcess ?? null,
        },
        company: {
          id: tx.company.id,
          featuredFacilityProcessId: tx.facilityProcess ?? null,
        },
        lines: (tx.lines ?? [])
          .map(line => ({
            productId: line.product?.id ?? line.productId ?? '',
            unit: line.unit ?? 'KILOGRAM',
            lots: (line.product?.lots ?? line.lots ?? []).map(lot => ({
              fiberLotId:
                ((lot as Record<string, unknown>).fiberLotId as
                  | string
                  | undefined) ??
                lot.id ??
                '',
              kgQuantity:
                ((lot as Record<string, unknown>).kgQuantity as
                  | number
                  | null
                  | undefined) ?? null,
              quantity: null,
            })),
          }))
          .filter(l => l.productId),
        documents: uploadedDocs,
      });
      const newId = res?.data?.id;
      toast.success(t('detail.receiveSuccess'));
      void navigate({
        to: '/transactions/incoming/$txId/overview',
        params: { txId: newId ?? txId },
      });
    } catch {
      toast.error(t('detail.receiveError'));
    }
  }

  if (isLoading) {
    return (
      <div className='flex flex-col gap-4 p-6'>
        <Skeleton className='h-32 w-full rounded-lg' />
        <Skeleton className='h-48 w-full rounded-lg' />
      </div>
    );
  }

  if (!tx) return null;

  const methodLabel =
    SHIPMENT_METHOD_LABELS[tx.transportationMethod ?? ''] ??
    tx.transportationMethod ??
    '—';

  const lineColumns: ColumnDef<OutgoingTransactionDetailLine>[] = [
    {
      id: 'product',
      header: t('detail.product'),
      cell: ({ row }) => (
        <span className='text-xs font-medium'>
          {row.original.product?.name ?? row.original.productName ?? '—'}
        </span>
      ),
    },
    {
      id: 'type',
      header: t('detail.type'),
      cell: ({ row }) => (
        <span className='text-xs text-muted-foreground'>
          {row.original.product?.productCategoryName ?? '—'}
        </span>
      ),
    },
    {
      id: 'secondaryQty',
      header: t('detail.secondaryQty'),
      cell: ({ row }) => {
        const qty = row.original.totalKgQuantity;
        return (
          <span className='text-xs font-medium'>
            {qty != null ? `${qty} kg` : '—'}
          </span>
        );
      },
    },
  ];

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      <IncomingTransactionDetailHeader
        transaction={tx}
        action={
          <Button
            onClick={() => void handleReceive()}
            loading={receiveMutation.isPending}
          >
            {t('detail.confirmReceive')}
          </Button>
        }
      />
      <div className='@container flex-1 p-4 sm:p-6 w-full'>
        <div className='flex flex-col gap-6'>
          <div className='grid grid-cols-1 @md:grid-cols-2 gap-4 items-stretch'>
            <FieldCard title={t('detail.transactionInfo')}>
              <FieldRow
                label={
                  <span className='text-xs text-muted-foreground'>
                    {t('detail.transactionNumber')}
                  </span>
                }
              >
                <span className='text-xs font-medium'>
                  {tx.transactionNumber ?? '—'}
                </span>
              </FieldRow>
              <FieldRow
                label={
                  <span className='text-xs text-muted-foreground'>
                    {t('detail.status')}
                  </span>
                }
              >
                <span className='text-xs'>{tx.status ?? '—'}</span>
              </FieldRow>
              <FieldRow
                label={
                  <span className='text-xs text-muted-foreground'>
                    {t('detail.transportMethod')}
                  </span>
                }
              >
                <span className='text-xs'>{methodLabel}</span>
              </FieldRow>
              <FieldRow
                label={
                  <span className='text-xs text-muted-foreground'>
                    {t('detail.transportId')}
                  </span>
                }
              >
                <span className='text-xs'>
                  {tx.transportationIdentification ?? '—'}
                </span>
              </FieldRow>
              <FieldRow
                label={
                  <span className='text-xs text-muted-foreground'>
                    {t('detail.date')}
                  </span>
                }
              >
                <span className='text-xs'>
                  {tx.transactionDate
                    ? formatDateDisplay(tx.transactionDate)
                    : '—'}
                </span>
              </FieldRow>
            </FieldCard>

            <ConnectedPartiesCard
              dividerLabel={t('detail.transactionFlow')}
              dividerIcon={<ArrowLeft className='size-3' />}
              topSlot={
                <PartyRow
                  label={t('detail.shipper')}
                  companyId={tx.withCompany.id}
                  name={tx.withCompany.name}
                  imageUrl={tx.withCompany.featuredImageFileUrl}
                  country={tx.withCompany.country}
                  facilityProcess={tx.withCompanyFacilityProcess}
                  viewLabel={t('detail.view')}
                />
              }
              bottomSlot={
                <PartyRow
                  label={t('detail.receiver')}
                  companyId={tx.company.id}
                  name={tx.company.name}
                  imageUrl={tx.company.featuredImageFileUrl}
                  country={tx.company.country}
                  facilityProcess={tx.facilityProcess}
                  viewLabel={t('detail.view')}
                />
              }
            />
          </div>

          <div className='flex flex-col gap-2'>
            <p className='text-sm font-semibold'>{t('detail.incomingLines')}</p>
            <div className='overflow-x-auto'>
              <DataTable
                data={tx.lines ?? []}
                columns={lineColumns}
                rowIdKey='id'
                disablePagination
                hideFooter
                emptyMessage={t('detail.noLines')}
              />
            </div>
          </div>

          {rules.length > 0 && (
            <div className='flex flex-col gap-2'>
              <p className='text-sm font-semibold'>
                {t('detail.requiredDocs')}
              </p>
              <DocumentRulesTable
                rules={rules}
                documents={documents}
                simpleColumns
                onFileSelected={handleFileSelected}
                onFileUploaded={handleFileUploaded}
                onRemoveFile={() => {}}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
